import { clsx } from 'clsx';
import { LAND_TERRAINS, getLandTerrain, getCircleSpellsForLevel } from '@character-creator/data';
import { getSpell } from '@character-creator/data';

interface LandCircleSelectorProps {
  druidLevel: number;
  selectedTerrainId: string | null;
  onSelect: (terrainId: string | null) => void;
  compact?: boolean;
}

export function LandCircleSelector({
  druidLevel,
  selectedTerrainId,
  onSelect,
  compact = false,
}: LandCircleSelectorProps) {
  const selectedTerrain = selectedTerrainId ? getLandTerrain(selectedTerrainId) : null;

  // Compact view - shows selected terrain with change option
  if (compact && selectedTerrain) {
    return (
      <div className="text-xs text-success flex items-center gap-1">
        <span>Land: {selectedTerrain.name}</span>
        <button
          type="button"
          onClick={() => onSelect(null)}
          className="ml-2 text-text-muted hover:text-error transition-colors"
        >
          (change)
        </button>
      </div>
    );
  }

  return (
    <div>
      <h5 className="text-sm font-medium text-text-primary mb-2">
        Circle of the Land
        {!selectedTerrainId && (
          <span className="text-warning ml-2 text-xs">(Required)</span>
        )}
      </h5>
      <p className="text-xs text-text-muted mb-3">
        Choose the type of land where you became a druid. This grants you additional circle spells.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {LAND_TERRAINS.map((terrain) => {
          // Get the circle spells available at current level
          const circleSpellIds = getCircleSpellsForLevel(terrain.id, druidLevel);
          const circleSpellNames = circleSpellIds
            .map((id) => getSpell(id)?.name)
            .filter(Boolean);

          return (
            <button
              key={terrain.id}
              type="button"
              onClick={() => onSelect(terrain.id)}
              className={clsx(
                'w-full text-left p-3 rounded-lg border transition-all duration-200',
                selectedTerrainId === terrain.id
                  ? 'bg-accent-gold/10 border-accent-gold'
                  : 'bg-background-secondary border-background-elevated hover:border-text-muted'
              )}
            >
              <div className="font-medium text-text-primary text-sm">{terrain.name}</div>
              <p className="text-xs text-text-secondary mt-1">{terrain.description}</p>
              {druidLevel >= 3 && circleSpellNames.length > 0 && (
                <div className="mt-2 pt-2 border-t border-background-elevated">
                  <div className="text-xs text-accent-gold">Circle Spells:</div>
                  <div className="text-xs text-text-muted">{circleSpellNames.join(', ')}</div>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
